import { useState } from 'react';
import * as R from 'ramda';

import MoveInventoryModal from '@atom/components/common/MoveInventoryModal';
import elementIcon from '@atom/components/common/svgIcons/elementIcon.svg';
import folderMoveIcon from '@atom/components/common/svgIcons/folderMove.svg';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Icon, List, Menu } from '@atom/mui';
import colors from '@atom/styles/colors';
import { AssetResult } from '@atom/types/search';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import {
  Client,
  isCurrentClient,
} from '@atom/utilities/featureToggleUtilities';
import { getSchemaIconFromSchemaOrAsset } from '@atom/utilities/schemaUtilities';
import { highlightedText } from '@atom/utilities/textDisplayUtilities';

import classes from './searchPortal.module.css';

const { MenuItem } = Menu;
const { ListItemButton, ListItem, ListItemText } = List;

const style = {
  listItem: {
    padding: 0,
    margin: 0,
    borderBottom: `1px solid ${colors.neutral.typhoon}`,
  },
  listItemButton: {
    height: '3.5rem',
  },
  primaryText: {
    fontWeight: 400,
  },
  elementIcon: {
    paddingLeft: '5px',
  },
};

interface Props {
  query: string;
  asset: AssetResult;
}

const InventoryResultRow = ({ query, asset }: Props) => {
  const [, { navigate }] = useNavigation();
  const [open, setOpen] = useState<boolean>(false);

  const handleNavigate = () => {
    const assetId = asset?.isSubItem
      ? R.pathOr('', ['ancestors', 0, 'id'], asset)
      : asset.id;

    navigate(`/inventory/${assetId}`);
  };

  const icon = asset?.isSubItem ? (
    <img style={style.elementIcon} src={elementIcon} />
  ) : (
    getSchemaIconFromSchemaOrAsset(asset)
  );
  const assetName = highlightedText(query, asset.name);
  const showMoveInventory = hasRolePermissions(
    isCurrentClient([Client.HENNEPIN])
      ? ROLE_SETS.MANAGER
      : ROLE_SETS.ORG_ADMIN,
  );
  const ancestorsDisplay = asset?.ancestors
    ?.map(ancestor => ancestor?.name)
    .join(' / ');
  const subtext = asset?.isSubItem
    ? `${asset?.assetType} - ${ancestorsDisplay}`
    : asset?.assetType;

  return (
    <>
      <ListItem
        style={style.listItem}
        key={asset.id}
        disablePadding
        secondaryAction={
          showMoveInventory ? (
            <Menu>
              <MenuItem
                startAdornment={
                  <Icon>
                    <img src={folderMoveIcon} />
                  </Icon>
                }
                onClick={() => setOpen(true)}
              >
                Move To Folder
              </MenuItem>
            </Menu>
          ) : (
            <div />
          )
        }
      >
        <ListItemButton
          disableGutters
          onClick={handleNavigate}
          style={style.listItemButton}
        >
          <div className={classes.iconContainerInventory}>{icon}</div>
          <ListItemText
            primary={assetName}
            primaryTextStyle={style.primaryText}
            secondary={subtext}
          />
        </ListItemButton>
      </ListItem>
      <MoveInventoryModal
        open={open}
        id={asset.id}
        name={asset.name}
        categoryId={R.last(asset.categoryPath)}
        schemaId={asset.schemaId}
        closeModal={() => setOpen(false)}
      />
    </>
  );
};

export default InventoryResultRow;
